import { z } from 'zod'

// FO-306 (batch-33, stream klant-skillmatrix) — gedeelde contract-types voor het
// klant-monteurprofiel ("monteur samenstellen"). Eén bron voor de klant-app
// (client-side validatie) én de API (parse), plus de DTO's die de klant en de
// backoffice teruggeven. De klant ziet NOOIT interne skill-niveaus, scores of
// kandidaten — die velden zitten bewust in geen enkele klant-DTO hieronder.

// ─── Enums (klant-facing) ─────────────────────────────────────────────────────

/** 3-puntsschaal die de klant kent (intern gemapt via mapKlantNiveau). */
export const KLANT_GEWENST_NIVEAUS = ['BASIS', 'GOED', 'EXPERT'] as const
export type KlantGewenstNiveau = (typeof KLANT_GEWENST_NIVEAUS)[number]

export const KLANT_GEWENST_NIVEAU_LABELS_NL: Record<KlantGewenstNiveau, string> = {
  BASIS: 'Basis',
  GOED: 'Goed',
  EXPERT: 'Expert',
}
export const KLANT_GEWENST_NIVEAU_LABELS_EN: Record<KlantGewenstNiveau, string> = {
  BASIS: 'Basic',
  GOED: 'Good',
  EXPERT: 'Expert',
}

/** Inzet-context als randvoorwaarde. */
export const KLANT_MONTEUR_INZET_CONTEXTEN = ['ONDERHOUD', 'STORING', 'PROJECT'] as const
export type KlantMonteurInzetContext = (typeof KLANT_MONTEUR_INZET_CONTEXTEN)[number]

export const KLANT_MONTEUR_INZET_CONTEXT_LABELS_NL: Record<KlantMonteurInzetContext, string> = {
  ONDERHOUD: 'Onderhoud',
  STORING: 'Storing',
  PROJECT: 'Project',
}

/**
 * Gewenst ervaringsniveau. Bewust gelijk aan de interne
 * `Employee.experienceLevel`-waarden zodat de behandelaar het 1:1 als hint op de
 * matchpagina herkent.
 */
export const KLANT_MONTEUR_ERVARINGEN = ['JUNIOR', 'MEDIOR', 'SENIOR', 'EXPERT'] as const
export type KlantMonteurErvaring = (typeof KLANT_MONTEUR_ERVARINGEN)[number]

export const KLANT_MONTEUR_ERVARING_LABELS_NL: Record<KlantMonteurErvaring, string> = {
  JUNIOR: 'Junior',
  MEDIOR: 'Medior',
  SENIOR: 'Senior',
  EXPERT: 'Expert',
}

/** Levenscyclus van een profiel. */
export const KLANT_MONTEUR_PROFIEL_STATUSSEN = [
  'CONCEPT',
  'INGEDIEND',
  'IN_BEHANDELING',
  'AFGEHANDELD',
  'INGETROKKEN',
] as const
export type KlantMonteurProfielStatus = (typeof KLANT_MONTEUR_PROFIEL_STATUSSEN)[number]

export const KLANT_MONTEUR_PROFIEL_STATUS_LABELS_NL: Record<KlantMonteurProfielStatus, string> = {
  CONCEPT: 'Concept',
  INGEDIEND: 'Ingediend',
  IN_BEHANDELING: 'In behandeling',
  AFGEHANDELD: 'Afgehandeld',
  INGETROKKEN: 'Ingetrokken',
}

/** Statuswaarden van de gekoppelde `ClientPortalRequest` (subset, klant-relevant). */
export type MonteurProfielRequestStatus =
  | 'INGEDIEND'
  | 'IN_BEHANDELING'
  | 'AFGEROND'
  | 'AFGEWEZEN'
  | 'INGETROKKEN'

/**
 * Afgeleide status: zodra er een gekoppelde aanvraag is, volgt de status die
 * aanvraag (zodat backoffice-statuswijzigingen automatisch doorwerken, zowel voor
 * de klant als op de relatie-detail). CONCEPT/INGETROKKEN zonder aanvraag komen van
 * de profiel-kolom zelf. Eén bron voor de klant-route én de backoffice-routes.
 */
export function deriveKlantMonteurProfielStatus(
  storedStatus: KlantMonteurProfielStatus,
  requestStatus: MonteurProfielRequestStatus | null,
): KlantMonteurProfielStatus {
  if (!requestStatus) return storedStatus
  switch (requestStatus) {
    case 'INGEDIEND':
      return 'INGEDIEND'
    case 'IN_BEHANDELING':
      return 'IN_BEHANDELING'
    case 'AFGEROND':
    case 'AFGEWEZEN':
      return 'AFGEHANDELD'
    case 'INGETROKKEN':
      return 'INGETROKKEN'
    default:
      return storedStatus
  }
}

// ─── Grenzen (§5) ─────────────────────────────────────────────────────────────

export const MONTEUR_PROFIEL_NAAM_MIN = 3
export const MONTEUR_PROFIEL_NAAM_MAX = 80
export const MONTEUR_PROFIEL_TOELICHTING_MAX = 2000
export const MONTEUR_PROFIEL_MAX_SKILLS = 15
export const MONTEUR_PROFIEL_MAX_CERTIFICATEN = 30
export const MONTEUR_PROFIEL_MAX_AANTAL = 999

// ─── Write-schema (klant-app + API, één bron) ─────────────────────────────────

const isoDate = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik het formaat jjjj-mm-dd')

/** Leeg string-veld ("") telt als "geen waarde" → null. */
const nullableIsoDate = z
  .union([isoDate, z.literal(''), z.null()])
  .optional()
  .transform((v) => (v === '' || v === undefined ? null : v))

export const monteurProfielSkillInputSchema = z.object({
  skillId: z.string().min(1),
  gewenstNiveau: z.enum(KLANT_GEWENST_NIVEAUS),
})
export type MonteurProfielSkillInput = z.infer<typeof monteurProfielSkillInputSchema>

export const monteurProfielCertificaatInputSchema = z.object({
  certificateTypeId: z.string().min(1),
})
export type MonteurProfielCertificaatInput = z.infer<
  typeof monteurProfielCertificaatInputSchema
>

/**
 * Volledig profiel-body (create én update = volledige vervanging; de wizard
 * stuurt het hele formulier). Veldnamen spiegelen de read-DTO zodat een
 * round-trip read → write nooit een onterechte VALIDATION_ERROR geeft.
 */
export const monteurProfielWriteSchema = z.object({
  naam: z.string().trim().min(MONTEUR_PROFIEL_NAAM_MIN).max(MONTEUR_PROFIEL_NAAM_MAX),
  toelichting: z
    .string()
    .trim()
    .max(MONTEUR_PROFIEL_TOELICHTING_MAX)
    .nullable()
    .optional()
    .transform((v) => (v === undefined ? null : v)),
  skills: z
    .array(monteurProfielSkillInputSchema)
    .min(1, 'Kies minimaal 1 kwalificatie')
    .max(MONTEUR_PROFIEL_MAX_SKILLS, `Maximaal ${MONTEUR_PROFIEL_MAX_SKILLS} kwalificaties`)
    .refine(
      (skills) => new Set(skills.map((s) => s.skillId)).size === skills.length,
      { message: 'Dezelfde kwalificatie is meerdere keren gekozen' },
    ),
  certificaten: z
    .array(monteurProfielCertificaatInputSchema)
    .max(MONTEUR_PROFIEL_MAX_CERTIFICATEN)
    .optional()
    .default([])
    .refine(
      (certs) =>
        new Set(certs.map((c) => c.certificateTypeId)).size === certs.length,
      { message: 'Hetzelfde certificaat is meerdere keren gekozen' },
    ),
  ervaring: z.enum(KLANT_MONTEUR_ERVARINGEN).nullable().optional().transform((v) => v ?? null),
  inzetContext: z
    .enum(KLANT_MONTEUR_INZET_CONTEXTEN)
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  gewenstePeriodeVan: nullableIsoDate,
  gewenstePeriodeTot: nullableIsoDate,
  startdatum: nullableIsoDate,
  aantalMonteurs: z
    .number()
    .int()
    .min(1)
    .max(MONTEUR_PROFIEL_MAX_AANTAL)
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  ploegendienst: z.boolean().optional().default(false),
  weekendwerk: z.boolean().optional().default(false),
})
export type MonteurProfielWriteInput = z.infer<typeof monteurProfielWriteSchema>

// ─── Klant-catalogus-DTO (bevat NOOIT interne schaal/scores) ──────────────────

export interface KlantCatalogusSkillDTO {
  id: string
  /** Klantlabel per locale, of de interne naam als fallback. */
  label: string
}

export interface KlantCatalogusGroepDTO {
  id: string
  label: string
  skills: KlantCatalogusSkillDTO[]
}

export interface KlantCatalogusCertificaatDTO {
  id: string
  label: string
}

export interface KlantCatalogusDTO {
  groepen: KlantCatalogusGroepDTO[]
  /** Zichtbare skills zonder (zichtbare) groep. */
  ongegroepeerd: KlantCatalogusSkillDTO[]
  certificaten: KlantCatalogusCertificaatDTO[]
  /** True = beheer heeft (nog) niets klant-zichtbaar gemarkeerd (lege-catalogus-signaal). */
  leeg: boolean
}

// ─── Klant-profiel-DTO ────────────────────────────────────────────────────────

export interface KlantMonteurProfielSkillDTO {
  skillId: string
  gewenstNiveau: KlantGewenstNiveau
  /** Actueel klantlabel per locale, of de opgeslagen snapshot als de skill verviel. */
  label: string
  /** False = skill is niet meer klant-zichtbaar (gemarkeerd bij bewerken, gestript bij indienen). */
  beschikbaar: boolean
}

export interface KlantMonteurProfielCertificaatDTO {
  certificateTypeId: string
  label: string
  beschikbaar: boolean
}

export interface KlantMonteurProfielDTO {
  id: string
  naam: string
  toelichting: string | null
  status: KlantMonteurProfielStatus
  ervaring: KlantMonteurErvaring | null
  inzetContext: KlantMonteurInzetContext | null
  gewenstePeriodeVan: string | null
  gewenstePeriodeTot: string | null
  startdatum: string | null
  aantalMonteurs: number | null
  ploegendienst: boolean
  weekendwerk: boolean
  skills: KlantMonteurProfielSkillDTO[]
  certificaten: KlantMonteurProfielCertificaatDTO[]
  requestId: string | null
  requestNumber: string | null
  /** Terugkoppeling van de behandelaar (vrije tekst; nooit scores/kandidaten). */
  terugkoppeling: string | null
  aangemaaktOp: string
  gewijzigdOp: string
  /** True als de klant het profiel nog mag bewerken (status CONCEPT). */
  bewerkbaar: boolean
  /** True als de klant het profiel nog mag intrekken (status INGEDIEND). */
  intrekbaar: boolean
}

export interface KlantMonteurProfielListItemDTO {
  id: string
  naam: string
  status: KlantMonteurProfielStatus
  aantalSkills: number
  requestNumber: string | null
  gewijzigdOp: string
}

// ─── Interne (backoffice) profiel-DTO ─────────────────────────────────────────
// Bevat interne skill-namen + gemapte minimumniveaus. NOOIT naar de klant-app.

export interface MonteurProfielInternSkillDTO {
  skillId: string
  skillNaam: string
  gewenstNiveau: KlantGewenstNiveau
  gewenstNiveauLabel: string
  schaalMin: number
  schaalMax: number
  /** Gemapt minimumniveau op de interne schaal (≥ 1), voor de match-input. */
  minLevelIntern: number
}

export interface MonteurProfielInternCertificaatDTO {
  certificateTypeId: string
  naam: string
  code: string
}

/** Lijst-item voor het relatie-detail-blok "Monteursprofielen" (backoffice). */
export interface MonteurProfielInternListItemDTO {
  id: string
  naam: string
  status: KlantMonteurProfielStatus
  aantalSkills: number
  requestId: string | null
  requestNumber: string | null
  matchUitgevoerdOp: string | null
  matchUitgevoerdDoorNaam: string | null
  gewijzigdOp: string
}

export interface MonteurProfielInternDTO {
  id: string
  clientId: string
  clientNaam: string
  naam: string
  toelichting: string | null
  status: KlantMonteurProfielStatus
  ervaring: KlantMonteurErvaring | null
  ervaringLabel: string | null
  inzetContext: KlantMonteurInzetContext | null
  inzetContextLabel: string | null
  gewenstePeriodeVan: string | null
  gewenstePeriodeTot: string | null
  startdatum: string | null
  aantalMonteurs: number | null
  ploegendienst: boolean
  weekendwerk: boolean
  skills: MonteurProfielInternSkillDTO[]
  certificaten: MonteurProfielInternCertificaatDTO[]
  requestId: string | null
  requestNumber: string | null
  matchUitgevoerdOp: string | null
  matchUitgevoerdDoorNaam: string | null
  /** Kant-en-klare runMatch-input voor de matchpagina (`POST /employees/match`). */
  matchInput: {
    requiredSkillIds: string[]
    minLevels: Record<string, number>
    targetDate: string | null
  }
}

// ─── Mapping-percentages (backoffice-instelling) ──────────────────────────────

export interface KlantNiveauMappingConfigDTO {
  basisPct: number
  goedPct: number
  expertPct: number
}

/**
 * Beheer-schema voor de organisatiebrede mapping-percentages. Oplopend
 * (Basis ≤ Goed ≤ Expert) en elk 0-100 zodat een niveau nooit buiten de schaal mapt.
 */
export const klantNiveauMappingConfigSchema = z
  .object({
    basisPct: z.number().int().min(0).max(100),
    goedPct: z.number().int().min(0).max(100),
    expertPct: z.number().int().min(0).max(100),
  })
  .refine((v) => v.basisPct <= v.goedPct && v.goedPct <= v.expertPct, {
    message: 'De percentages moeten oplopen: Basis ≤ Goed ≤ Expert',
    path: ['goedPct'],
  })
export type KlantNiveauMappingConfigInput = z.infer<typeof klantNiveauMappingConfigSchema>

// ─── Catalogus-beheer (per skill / groep / certificaattype) ───────────────────

/** Gedeeld sub-schema voor de klant-catalogus-vlaggen op skill/groep/certificaattype. */
export const catalogusVlaggenSchema = z.object({
  zichtbaarVoorKlant: z.boolean(),
  klantLabelNl: z.string().trim().max(120).nullable().optional().transform((v) => v ?? null),
  klantLabelEn: z.string().trim().max(120).nullable().optional().transform((v) => v ?? null),
})
export type CatalogusVlaggenInput = z.infer<typeof catalogusVlaggenSchema>
